import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { Link, useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextArea from "@/common/components/TextArea";
import { ShowHeader } from "@/common/components/ShowHeader";
import useTmaisRaffle from "../../hooks/useTmaisRaffle";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { formatDate, formatTime } from "@/common/utils/formatters";
import { winnerOperationKlassStrategy } from "../../constants/winnerOperationKlassOptions.constant";
import { useRewardTicketsInfinite } from "@/common/hooks/queries/useRewardTicketsInfinite";
import RewardTickets from "../../components/RewardTickets";
import { RewardTicketData } from "@/common/types/rewardTickets.type";

const Show = () => {
  const { id } = useParams();

  const {
    data: initialRaffle,
    isPending: initialRaffleIsPending,
    error: initialRaffleError = {} as any,
    refetch: refetchRaffle,
  } = useTmaisRaffle(id || '');

  const {
    data: rewardTickets,
    isPending: rewardTicketsIsPending,
    hasNextPage: hasNextPageRewardTickets,
    isFetchingNextPage: isFetchingNextPageRewardTickets,
    fetchNextPage: fetchNextPageRewardTickets,
  } = useRewardTicketsInfinite<RewardTicketData[]>({
    id: id || '',
    filters: {
      winPositionNotNull: true
    }
  });

  const disableUpdateButton = initialRaffleIsPending || initialRaffleError?.status === 403;

  const hasSomeRewardTicketWithWinPosition = rewardTickets?.some((ticket) => ticket.win_position !== null)

  const formattedPointsPerTickets = initialRaffle?.points_per_ticket?.toLocaleString()
  const formattedNumberOfTickets = initialRaffle?.number_of_tickets?.toLocaleString()
  const formattedMaxTicketsPerUser = initialRaffle?.max_tickets_per_user?.toLocaleString()
  
  const strategyKey = initialRaffle?.select_winner_operation_klass as keyof typeof winnerOperationKlassStrategy
  const strategyName = strategyKey ? winnerOperationKlassStrategy[strategyKey] : undefined
  
  const winnerDateObject = initialRaffle?.select_winner_at ? new Date(initialRaffle.select_winner_at) : null
  const formattedWinnerDate = winnerDateObject ? formatDate(winnerDateObject) : undefined
  const formattedWinnerTime = winnerDateObject ? formatTime(winnerDateObject, false) : undefined
  
  const availableFromDate = initialRaffle?.available_from ? formatDate(new Date(initialRaffle.available_from)) : undefined
  const availableUntilDate = initialRaffle?.available_until ? formatDate(new Date(initialRaffle.available_until)) : undefined

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-raffles` }}
        title="Sorteios"
        subtitle={initialRaffle?.display_name}
        buttons={(
          <div className="flex items-center gap-2">
            <Link
              to={disableUpdateButton ? '' : `/tmais-raffles/${id}/edit`}
              className={disableUpdateButton ? "cursor-default" : ''}
              >
              <Button operationKlassToCheckPermission="Establishment::Tmais::Raffles::Update" data-testid='update_raffle_button' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" disabled={disableUpdateButton}>
                Editar
              </Button>
            </Link>
          </div>
        )}
      />

      {initialRaffleIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={initialRaffleError?.status}
          refetchFunction={refetchRaffle}
        >
          <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <h2 className="text-lg font-bold">Detalhes do Sorteio</h2>
            <h2 className="text-base font-bold">Nome e imagens do sorteio</h2>

            <div className="flex gap-4">
              {initialRaffle?.pictures?.length ? (
                <ImageCarousel 
                  files={initialRaffle.pictures.map((photo, i) => ({ id: String(i), file: photo.url }))} 
                  showRemoveButton={false} 
                />
              ) : null}
              <div className="flex flex-col gap-4 w-full">
                <TextField
                  label="Nome"
                  className="w-full"
                  value={initialRaffle?.display_name}
                  disabled
                />
  
                <TextArea
                  label="Descrição"
                  rows={8}
                  value={initialRaffle?.description}
                  disabled
                />
              </div>
            </div>

            <h2 className="text-base font-bold">Configurações do sorteio</h2>
            
            <div className="flex items-start gap-2">
              <TextField
                label="Custo em pontos do bilhete"
                className="w-full"
                value={formattedPointsPerTickets}
                disabled
              />
              <TextField
                label="Quantidade de bilhetes"
                className="w-full"
                value={formattedNumberOfTickets}
                disabled
              />
              <TextField
                label="Máximo de bilhetes por usuário"
                className="w-full"
                value={formattedMaxTicketsPerUser}
                disabled
              />
            </div>

            <div className="flex items-start gap-2">
              <TextField
                label="Forma do sorteio"
                className="w-1/2"
                value={strategyName}
                disabled
              />
              <div className="flex w-1/2 gap-2">
                <TextField
                  label="Dia do sorteio"
                  className="w-1/2"
                  value={formattedWinnerDate}
                  disabled
                />
                <TextField
                  label="Hora do sorteio"
                  className="w-1/2"
                  value={formattedWinnerTime}
                  disabled
                />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <TextField
                label="Data inicial para participação"
                className="w-1/2"
                value={availableFromDate}
                disabled
              />
              <TextField
                label="Data final para participação"
                className="w-1/2"
                value={availableUntilDate}
                disabled
              />
            </div>
            <RewardTickets 
              rewardTickets={rewardTickets} 
              raffleId={id} 
              hasSomeRewardTicketWithWinPosition={hasSomeRewardTicketWithWinPosition}
              isPending={rewardTicketsIsPending}
              hasNextPage={hasNextPageRewardTickets}
              isFetchingNextPage={isFetchingNextPageRewardTickets}
              fetchNextPage={fetchNextPageRewardTickets}
            />
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  );
};

export default Show;
